import {Component, Input, OnChanges, OnInit} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { CartItemComponentOptions, CartItemContext, PromotionLocation, CartOutlets } from '@spartacus/cart/base/root';
import {
  Product,
  SiteContextActions,
  useFeatureStyles,
  UserIdService
} from '@spartacus/core';
import { GonvarriCartItemContextSource } from 'src/app/core/models/gonvarri-item-context-source.model';
import { ICON_TYPE } from '@spartacus/storefront';
import { GonvarriCartEntry } from 'src/app/core/models/gonvarri-cart-entry.model';
import { GonvarriActiveCartService } from 'src/app/core/services/gonvarri-active-cart.service';
import { Store } from '@ngrx/store';
import { CartActions, StateWithMultiCart } from '@spartacus/cart/base/core';
import {Actions, ofType} from "@ngrx/effects";
import {GonvarriCartUpdateEntry} from "../../core/actions/gonvarri-cart-update-entry-success";
import {map} from "rxjs/operators";

@Component({
    selector: '[cx-cart-item-list-row]',
    templateUrl: './gonvarri-cart-item-list-row.component.html',
    styleUrl: './gonvarri-cart-item-list-row.component.scss',
    providers: [
        GonvarriCartItemContextSource,
        { provide: CartItemContext, useExisting: GonvarriCartItemContextSource },
    ],
    standalone: false
})
export class GonvarriCartItemListRowComponent implements OnChanges, OnInit {

  curQuantity: number = 0;
  cartIsLoading$ = this.activeCartService.isStable().pipe(map(loaded => {
    if(!loaded && this.cartId) {
      this.activeCartService.load(this.cartId, this.userId);
    }
    return !loaded;
  }))

  @Input() cartId: string | undefined = undefined;
  @Input() isSeller: boolean = false;
  @Input() compact = false;
  @Input() item: GonvarriCartEntry = {} as GonvarriCartEntry;
  @Input() readonly = false;
  @Input() enableAddToCart: boolean = false;
  @Input() quantityControl: UntypedFormControl = new UntypedFormControl();
  @Input() isAsturias: boolean = false;
  @Input() isQuote: boolean = false;

  @Input() promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;

  @Input() options: CartItemComponentOptions = {
    isSaveForLater: false,
    optionalBtn: null,
    displayAddToCart: false,
  };

  iconTypes = ICON_TYPE;
  readonly CartOutlets = CartOutlets;

  private contextChange$ = this.actions$.pipe(
    ofType(
      SiteContextActions.CURRENCY_CHANGE,
      SiteContextActions.LANGUAGE_CHANGE
    )
  );

  userId: string = "";
  isCartRemoved: boolean = false;

  constructor(
    protected cartItemContextSource: GonvarriCartItemContextSource,
    private activeCartService: GonvarriActiveCartService,
    userIdService: UserIdService,
    protected store: Store<StateWithMultiCart>,
    private actions$: Actions,
  ) {
    useFeatureStyles('a11yCartItemsLinksStyles');
    userIdService.getUserId().subscribe(userId => {
      this.userId = userId;
    });
  }

  ngOnInit() {
    if(this.cartId === undefined) {
      this.activeCartService.getActive().subscribe(cart => {
        this.cartId = cart.code as string;
      });
    }
  }

  ngOnChanges(changes?: any) {
    if (changes) {
      if (changes.compact) {
        this.cartItemContextSource.compact$.next(this.compact);
      }
      if (changes.readonly) {
        this.cartItemContextSource.readonly$.next(this.readonly);
      }
      if (changes.item) {
        this.cartItemContextSource.item$.next(this.item);
      }
      if (changes.quantityControl) {
        this.cartItemContextSource.quantityControl$.next(this.quantityControl);
      }
      if (changes.promotionLocation) {
        this.cartItemContextSource.location$.next(this.promotionLocation);
      }
      if (changes.options) {
        this.cartItemContextSource.options$.next(this.options);
      }
    }
  }

  isProductOutOfStock(product: any): boolean {
    return (
      product &&
      product.stock &&
      product.stock.stockLevelStatus === 'outOfStock'
    );
  }

  removeItem() {

    this.quantityControl.setValue(0);
    this.quantityControl.markAsDirty();
    if(this.cartId !== undefined) {
      this.activeCartService.load(this.cartId, this.userId);
    }
    const subscription = this.activeCartService.getEntries().subscribe(entries => {
      if (entries.length === 0 && this.isCartRemoved === false) {
        this.isCartRemoved = true;
        if(this.cartId !== undefined) {
          this.store.dispatch(new CartActions.RemoveCart({cartId: this.cartId}));
        }
      }
    });
    if(this.cartId !== undefined) {
      this.store.dispatch(new CartActions.LoadCart({cartId: this.cartId, userId: this.userId}));
    }
  }

  getStockLevel(): any {
    return this.item.product?.stock?.stockLevel;
  }
  getProductCode(): any {
    return this.item.product?.code;
  }
  getPromotions(): any {
    return this.item.promotions;
  }
  getProductImage(): any {
    const images = this.item.product?.images ?? {};
    return images['PRIMARY'];
  }
  getReadonly(): any {
    return (
      !this.item.updateable || this.readonly || this.options.isSaveForLater
    );
  }
  getVariantQualifiers(item: any): any[] {
    const baseOptions = this.item.product !== undefined ? this.item.product.baseOptions : [];
    const firstOption = baseOptions !== undefined && baseOptions.length > 0 ? baseOptions[0].selected?.variantOptionQualifiers : undefined;
    return firstOption ?? [];
  }

  calculateTotalWeight(): number {
    const weight = this.item.packageWeight;
    return isNaN(Number(weight) * Number(this.item.quantity)) ? 0 :
      Number(weight) * Number(this.item.quantity);
  }

  getMax(): any {
    return this.item.product?.stock?.stockLevel;
  }
  
  getDiscountValueForEntry(entry: GonvarriCartEntry): string {
    return entry.discountValuesList !== undefined && entry.discountValuesList.length > 0
      ? entry.newPriceWithDiscount?.formattedValue + " / Tm" : "N/A";
  }

  getNumberOfSheets(): number {
    return this.item.numberOfSheets as number;
  }

  getProduct(): Product {
    return this.item.product as Product;
  }

  updateNumberOfSheets(values: number[]): void {
    this.curQuantity = values[1];
  }

  updateEntryPackageValue(value: KeyboardEvent): void {
    if(value.code.includes('Enter') && this.cartId) {
      this.store.dispatch(new GonvarriCartUpdateEntry({
        entryNumber: this.item.entryNumber?.toString() as string,
        unitType: this.item?.unit?.unitType !== undefined ? this.item?.unit?.unitType.toLowerCase() : "",
        curQuantity: this.curQuantity,
        customLength: this.item.customLength as number,
        productCode: this.item.product?.code as string,
        quantity: this.item.quantity as number,
        userId: this.userId,
        cartId: this.cartId
      }));
    }
  }

  protected readonly isNaN = isNaN;
}
